// Ported from design/project/App77-ChartFull.dc.html (upright) and App78-ChartLandscape.dc.html (sideways).
// The only screen that may turn sideways: drag, pinch and hold for details; LONG / SHORT only while upright.
import { useEffect, useRef, useState } from "react";
import { CandleChart, chartArena, saveTimeframe, savedTimeframe, type ChartHandle, type Ohlc, type Timeframe } from "../chart.js";
import { fmtPrice, lastPrice } from "../format.js";
import { useGame } from "../game.js";
import { allowRotation } from "../telegram.js";
import { CallButtons, ComingSoon, DECIMALS, Tabs, usePick } from "./App76-Chart.js";

function useSideways(): boolean {
  const q = () => matchMedia("(orientation: landscape)").matches;
  const [on, setOn] = useState(q);
  useEffect(() => {
    const m = matchMedia("(orientation: landscape)");
    const f = () => setOn(m.matches);
    m.addEventListener("change", f);
    return () => m.removeEventListener("change", f);
  }, []);
  return on;
}

export function App77_ChartFull() {
  const { selection, prices, arenas } = useGame();
  const arena = chartArena(selection.arena);
  const sideways = useSideways();
  const [tf, setTfState] = useState<Timeframe>(savedTimeframe);
  const setTf = (t: Timeframe) => {
    saveTimeframe(t);
    setTfState(t);
  };
  const [held, setHeld] = useState<Ohlc | null>(null);
  const [away, setAway] = useState(false);
  const handle = useRef<ChartHandle | null>(null);
  const { pick, busy, error } = usePick();
  useEffect(() => {
    allowRotation(true);
    return () => allowRotation(false);
  }, []);
  const d = DECIMALS[arena] ?? 2;
  const live = lastPrice(prices[arena]);
  const open = arenas.find((a) => a.id === arena)?.open ?? false;
  const pair = arena === "XAU" ? "GOLD" : `${arena}/USD`;
  const p = (n: number) => fmtPrice(arena, n);
  const chg = held && held.o ? ((held.c - held.o) / held.o) * 100 : 0;
  const closeColour = held && held.c < held.o ? "#FF3366" : "#CCFF00";
  // Telegram's own buttons sit at the top (and, sideways, the left): the chart keeps clear of both.
  const side = "max(16px, calc(var(--tg-safe-area-inset-left, 0px) + var(--tg-content-safe-area-inset-left, 0px) + 8px))";
  const sideR = "max(16px, calc(var(--tg-safe-area-inset-right, 0px) + var(--tg-content-safe-area-inset-right, 0px) + 8px))";

  const exit = (
    <button onClick={() => history.back()} className="ib" aria-label="Exit full screen" style={{ cursor: "pointer", width: "34px", height: "34px", flex: "none" }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block" }} aria-hidden="true"><path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5"></path></svg></button>
  );
  const liveChip = <span className="chip chipL" style={{ height: "22px", fontSize: "10px", gap: "6px", flex: "none" }}><span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#CCFF00" }}></span>LIVE</span>;

  return (
    <>
      {/* Sideways, the chart takes the whole width instead of the phone-width column every other screen keeps. */}
      <div className="screen" style={sideways ? { maxWidth: "none" } : undefined}>
  <div className="chartRoot" style={{ position: "relative", padding: sideways ? `calc(var(--tg-safe-area-inset-top, 0px) + var(--tg-content-safe-area-inset-top, 46px) + 4px) ${sideR} calc(var(--tg-safe-area-inset-bottom, 0px) + 8px) ${side}` : "var(--dd-safe-top) 16px calc(var(--dd-nav-bottom) + 6px)" }}>
    {/* Sideways, the title row sits in Telegram's strip at the top, between its Back and menu buttons, like every
        upright screen's title does; Telegram's Back leaves full screen. Upright, it has its own row with an exit button. */}
    <div style={sideways
      ? { position: "absolute", zIndex: "4", top: "calc(var(--tg-safe-area-inset-top, 0px) + (var(--tg-content-safe-area-inset-top, 46px) - 34px) / 2)", left: "120px", right: "120px", height: "34px", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", minWidth: "0" }
      : { display: "flex", alignItems: "center", gap: "10px", minWidth: "0" }}>
      {!sideways && exit}
      <span className="syne" style={{ fontWeight: "800", fontSize: "15px", whiteSpace: "nowrap" }}>{pair}</span>
      <span className="mono" style={{ fontWeight: "800", fontSize: "15px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: "0" }}>{fmtPrice(arena, live)}</span>
      {liveChip}
      {sideways ? <div style={{ flex: "none", marginLeft: "8px" }}><Tabs tf={tf} set={setTf} compact /></div> : <span style={{ flex: "1" }}></span>}
    </div>
    {!sideways && (
      <div style={{ marginTop: "8px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
        <Tabs tf={tf} set={setTf} compact />
        <span className="muted" style={{ fontSize: "11px", textAlign: "right", lineHeight: "14px" }}>Drag · pinch to zoom<br />hold for details</span>
      </div>
    )}

    <div style={{ marginTop: "8px", flex: "1", minHeight: "0", position: "relative" }}>
      <CandleChart arena={arena} tf={tf} interactive decimals={d} onHover={setHeld} onAway={setAway} onReady={(h) => (handle.current = h)} />
      {held && (
        <div style={{ position: "absolute", top: "6px", left: "8px", zIndex: "3", pointerEvents: "none", padding: sideways ? "6px 10px" : "8px 10px", borderRadius: "12px", background: "rgba(11,14,20,0.92)", border: "1px solid rgba(255,255,255,0.12)", fontSize: "11px",
          ...(sideways ? { display: "flex", gap: "10px", whiteSpace: "nowrap" } : { display: "grid", gridTemplateColumns: "auto auto", gap: "2px 10px" }) }}>
          {sideways ? (
            <>
              <span><span className="muted">O</span> <span className="mono">{p(held.o)}</span></span>
              <span><span className="muted">H</span> <span className="mono">{p(held.h)}</span></span>
              <span><span className="muted">L</span> <span className="mono">{p(held.l)}</span></span>
              <span><span className="muted">C</span> <span className="mono" style={{ color: closeColour }}>{p(held.c)} ({chg >= 0 ? "+" : ""}{chg.toFixed(2)}%)</span></span>
            </>
          ) : (
            <>
              <span className="muted">Open</span><span className="mono">{p(held.o)}</span>
              <span className="muted">High</span><span className="mono">{p(held.h)}</span>
              <span className="muted">Low</span><span className="mono">{p(held.l)}</span>
              <span className="muted">Close</span><span className="mono" style={{ color: closeColour }}>{p(held.c)} ({chg >= 0 ? "+" : ""}{chg.toFixed(2)}%)</span>
            </>
          )}
        </div>
      )}
      {away && (
        <button onClick={() => handle.current?.now()} style={{ position: "absolute", right: "64px", bottom: "30px", zIndex: "3", height: "30px", padding: "0 12px", borderRadius: "15px", border: "1px solid rgba(204,255,0,0.5)", background: "rgba(11,14,20,0.9)", color: "#CCFF00", fontFamily: "Inter, sans-serif", fontSize: "12px", fontWeight: "700", display: "flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
          Now<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#CCFF00" strokeWidth="3" strokeLinecap="round" style={{ display: "block" }} aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"></path></svg></button>
      )}
    </div>

    {!sideways && (
      <div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "10px" }}>
        {error === "low_balance" && <span role="alert" style={{ color: "#FF5C85", fontSize: "13px", textAlign: "center" }}>Not enough balance for this stake.</span>}
        {open ? <CallButtons height="52px" onPick={(s) => void pick(s)} disabled={busy} /> : <ComingSoon />}
      </div>
    )}
  </div>
</div>
    </>
  );
}
